/**
 * Ponašanje „nazad" dok je otvoren panel / modal.
 *
 * Na Androidu sistemsko „nazad" (dugme ili gest) emituje `popstate`, što
 * react-router inače protumači kao „vrati se na prethodnu stranicu". Ako je u
 * tom trenutku otvoren desni side bar (ili bilo koji modal preko celog ekrana),
 * očekivano je da „nazad" samo zatvori taj panel - isto kao tap na overlay.
 *
 * Overlay-i u aplikaciji imaju „overlay" u nazivu klase, `position: fixed`,
 * prekrivaju (skoro) ceo ekran i klik na njih zatvara panel koji prekrivaju.
 * Zato ih ovde prepoznajemo po tome, a „nazad" odradi upravo taj klik.
 */

const OVERLAY_SELECTOR = '[class*="overlay"]';

// Koliki deo ekrana overlay mora da prekriva da bi se smatrao panelom/modal-om
// (isključuje male ukrasne „overlay" elemente, npr. bedž na kartici obroka).
const COVERAGE = 0.6;

const isFullscreenOverlay = (el) => {
  if (!el || el.getAttribute('aria-hidden') === 'true') return false;
  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  if (style.position !== 'fixed') return false;
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return false;
  return (
    rect.width >= window.innerWidth * COVERAGE &&
    rect.height >= window.innerHeight * COVERAGE
  );
};

/**
 * Vraća overlay najgornjeg otvorenog panela/modal-a (poslednji u DOM-u), ili
 * `null` ako ništa nije otvoreno.
 */
export const findOpenOverlay = () => {
  const nodes = document.querySelectorAll(OVERLAY_SELECTOR);
  for (let i = nodes.length - 1; i >= 0; i -= 1) {
    if (isFullscreenOverlay(nodes[i])) return nodes[i];
  }
  return null;
};

/**
 * Zatvara najgornji otvoreni panel „klikom" na njegov overlay (isto kao da je
 * korisnik tapnuo van panela). Vraća `true` ako je nešto bilo otvoreno.
 */
export const dismissOpenOverlay = () => {
  const overlay = findOpenOverlay();
  if (!overlay) return false;
  overlay.click();
  return true;
};

/* ------------------------------------------------------------------------- */
/*  Istorijski „štit"                                                        */
/* ------------------------------------------------------------------------- */
/* Da bi sistemsko „nazad" moglo da zatvori panel, a da se stranica pri tome  */
/* ne promeni, u istoriju se (dok je panel otvoren) ubaci dodatni unos sa     */
/* ISTOM adresom. „Nazad" tako prvo potroši taj unos, react-router vidi       */
/* nepromenjenu adresu i ne menja stranicu, a mi u tom trenutku zatvorimo     */
/* panel.                                                                     */

const GUARD_FLAG = '__overlayBackGuard';

let guardArmed = false;

export const isOverlayGuardArmed = () => guardArmed;

export const consumeOverlayGuard = () => {
  guardArmed = false;
};

export const armOverlayGuard = () => {
  // Ako je naš unos već na vrhu istorije - ništa se ne radi.
  if (window.history.state && window.history.state[GUARD_FLAG]) {
    guardArmed = true;
    return;
  }
  try {
    window.history.pushState(
      { ...(window.history.state || {}), [GUARD_FLAG]: true },
      '',
      window.location.href
    );
    guardArmed = true;
  } catch (e) {
    // Ako pushState ne uspe, bolje da ostanemo neaktivni nego da blokiramo
    // normalno „nazad".
    guardArmed = false;
  }
};

export const disarmOverlayGuard = () => {
  if (!guardArmed) return;
  guardArmed = false;
  // Skini dodatni unos (samo ako je naš i dalje na vrhu istorije) da posle
  // zatvaranja panela kroz UI ne ostane „mrtav" pritisak na nazad.
  if (window.history.state && window.history.state[GUARD_FLAG]) {
    window.history.back();
  }
};
